import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthStudent } from "../../../../../context/StudentLoginContext/StudentAuthContext";
import { Modal } from "react-bootstrap";
import "./player.css";
import { useAxios } from "../../../../../../utils/useAxios";
import { TENANT_DOMAIN } from "../../../../../../utils/domain";

const SCRIPT_ID = "jwplayer-script";
const JW_LIB = "https://cdn.jwplayer.com/libraries/8LwdHpsM.js";

// ---------- Helper: read tenant-user locale settings ----------
const getTenantLocaleSettings = () => {
  try {
    const raw = localStorage.getItem("tenant-user");
    if (!raw) return { canChangeLocale: true, locale: "en" };
    const parsed = JSON.parse(raw);
    return {
      canChangeLocale: parsed?.can_change_locale === true,
      locale: parsed?.locale || "en",
    };
  } catch {
    return { canChangeLocale: true, locale: "en" };
  }
};

const JWPlayerStudentMultiLanguage = ({
  videoSources,
  nextEpisodeId,
  videoStartTime,
  chapters,
  type,
  setIsPaused,
}) => {
  const playerContainerRef = useRef(null);
  const setupDebounceRef = useRef(null);

  // ---------- Locale settings from tenant-user ----------
  const { canChangeLocale, locale: tenantLocale } = getTenantLocaleSettings();

  // If can_change_locale is false, force language to tenant locale; else use localStorage or default "en"
  const getInitialLanguage = () => {
    if (!canChangeLocale) return tenantLocale;
    return localStorage.getItem("language") || "en";
  };

  const [language, setLanguage] = useState(getInitialLanguage);
  const [prevLanguage, setPrevLanguage] = useState(getInitialLanguage);
  const [errorMsg, setErrorMsg] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [playerInstance, setPlayerInstance] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const navigate = useNavigate();
  const { axiosInstance } = useAxios();
  const { student } = useAuthStudent();
  const { questId, chapterId } = useParams();

  const videoTimeStamp = Number(chapters?.remaining_time?.videoTimeStamp || 0);
  const videoCompleteTime = Number(
    chapters?.remaining_time?.videoCompleteTime || 1,
  );
  const watchedPercentage = (videoTimeStamp / videoCompleteTime) * 100;

  // ---------- Utility: safe destroy ----------
  const destroyPlayer = () => {
    try {
      if (!window.jwplayer) return;
      const maybeInstance = window.jwplayer && window.jwplayer("jw-player");
      if (
        maybeInstance &&
        typeof maybeInstance === "object" &&
        maybeInstance.id
      ) {
        maybeInstance.remove();
      }
    } catch (e) {}
    try {
      const node = playerContainerRef.current;
      if (node) node.innerHTML = "";
    } catch (e) {}
    setPlayerInstance(null);
  };

  // ---------- Load JW script once ----------
  useEffect(() => {
    if (window.jwplayer) return;
    let existing = document.getElementById(SCRIPT_ID);
    if (existing) return;

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = JW_LIB;
    script.async = true;
    script.onload = () => {
      console.log("JWPlayer library loaded");
    };
    script.onerror = () => {
      setErrorMsg("Failed to load video player. Please try again later.");
    };
    document.body.appendChild(script);
  }, []);

  // ---------- Helper to validate URL ----------
  const isValidUrl = (u) => typeof u === "string" && u.length > 8;

  // ---------- Main setup effect (debounced) ----------
  useEffect(() => {
    const validSources =
      videoSources && typeof videoSources === "object" ? videoSources : null;
    const url = validSources ? validSources[language] : null;

    if (!isValidUrl(url)) {
      console.log(
        "JW: waiting for valid video url for language:",
        language,
        validSources,
      );
      return;
    }

    if (setupDebounceRef.current) clearTimeout(setupDebounceRef.current);
    setupDebounceRef.current = setTimeout(() => {
      (async () => {
        setErrorMsg("");

        try {
          if (document.fullscreenElement) await document.exitFullscreen();
          else if (document.webkitFullscreenElement)
            window.webkitExitFullscreen?.();
          else if (document.mozFullScreenElement)
            window.mozCancelFullScreen?.();
          else if (document.msFullscreenElement) window.msExitFullscreen?.();
        } catch (e) {}

        destroyPlayer();

        const container = playerContainerRef.current;
        if (!container) {
          setErrorMsg("Video container not available.");
          return;
        }
        container.innerHTML = "";
        const div = document.createElement("div");
        div.id = "jw-player";
        container.appendChild(div);

        if (!window.jwplayer) {
          setErrorMsg("Video player failed to load. Please refresh the page.");
          return;
        }

        const localizationOptions = {
          en: { menu: "Menu", play: "Play", pause: "Pause" },
          hi: { menu: "मेनू", play: "चलाएं", pause: "रोकें" },
        };

        let player;
        try {
          player = window.jwplayer("jw-player").setup({
            file: url,
            width: "100%",
            aspectratio: "16:9",
            autostart: "viewable",
            mute: false,
            controls: true,
            preload: "auto",
            localization:
              localizationOptions[language] || localizationOptions.en,
          });
        } catch (err) {
          setErrorMsg("Player initialization failed.");
          return;
        }

        setPlayerInstance(player);

        player.on("error", (err) => {
          console.error("JWPLAYER ERROR", err);
          setErrorMsg(
            "Video failed to load. Please check your internet connection or try a different browser/device.",
          );
        });

        // ---------- READY: language overlay (only if canChangeLocale) ----------
        player.on("ready", () => {
          try {
            const containerEl = document.querySelector("#jw-player");
            if (!containerEl) return;
            containerEl.style.position = "relative";

            const prevOverlay = containerEl.querySelector(".language-overlay");
            if (prevOverlay) prevOverlay.remove();

            // Only inject the in-player overlay when user is allowed to change language
            if (canChangeLocale) {
              const overlay = document.createElement("div");
              overlay.style.position = "absolute";
              overlay.style.color = "#ffffff";
              overlay.style.cursor = "pointer";
              overlay.style.fontFamily = "sans-serif";
              overlay.classList.add("language-overlay");

              const displayText = document.createElement("div");
              displayText.textContent = "Language";
              displayText.style.border = "1px solid #80CAFF";
              displayText.style.borderRadius = "4px";
              displayText.style.background = "rgba(0, 0, 0, 0.6)";
              displayText.style.display = "inline-block";
              displayText.classList.add("font-pad");

              const dropdown = document.createElement("div");
              dropdown.style.position = "absolute";
              dropdown.style.bottom = "30px";
              dropdown.style.right = "0";
              dropdown.style.background = "rgba(0, 0, 0, 0.8)";
              dropdown.style.border = "1px solid #80CAFF";
              dropdown.style.borderRadius = "4px";
              dropdown.style.display = "none";
              dropdown.style.flexDirection = "column";
              dropdown.style.minWidth = "100px";

              const langs = [
                { value: "en", label: "English" },
                { value: "hi", label: "Hindi" },
              ];
              langs.forEach(({ value, label }) => {
                const opt = document.createElement("div");
                opt.textContent = label;
                opt.style.padding = "5px 10px";
                opt.style.cursor = "pointer";
                opt.style.color = "#80CAFF";
                opt.addEventListener("click", (e) => {
                  e.stopPropagation();
                  setPrevLanguage(language);
                  setLanguage(value);
                  localStorage.setItem("language", value);
                  dropdown.style.display = "none";
                });
                dropdown.appendChild(opt);
              });

              displayText.addEventListener("click", (e) => {
                e.stopPropagation();
                dropdown.style.display =
                  dropdown.style.display === "none" ? "block" : "none";
              });

              document.addEventListener(
                "click",
                (e) => {
                  if (!overlay.contains(e.target)) {
                    dropdown.style.display = "none";
                  }
                },
                { passive: true },
              );

              overlay.appendChild(displayText);
              overlay.appendChild(dropdown);
              containerEl.appendChild(overlay);

              let hideTimeout;
              const showOverlay = () => {
                if (hideTimeout) clearTimeout(hideTimeout);
                overlay.style.opacity = "1";
              };
              const scheduleHideOverlay = () => {
                if (hideTimeout) clearTimeout(hideTimeout);
                hideTimeout = setTimeout(() => {
                  overlay.style.opacity = "0";
                  dropdown.style.display = "none";
                }, 2000);
              };
              containerEl.addEventListener("mousemove", () => {
                showOverlay();
                scheduleHideOverlay();
              });
              containerEl.addEventListener("mouseenter", showOverlay);
            }

            container.scrollIntoView({ behavior: "smooth", block: "center" });
          } catch (e) {}
        });

        // Track time
        player.on("time", (ev) => {
          setCurrentTime(ev.position);
        });

        // Restore progress / seek
        player.on("ready", () => {
          try {
            if (prevLanguage !== language && currentTime > 0) {
              player.seek(currentTime);
            } else {
              if (watchedPercentage === 100) {
                player.seek(0);
                setShowModal(true);
              } else if (Number(videoStartTime) >= 0) {
                if (Number(videoStartTime) > 0)
                  player.seek(Number(videoStartTime));
              }
            }
          } catch (e) {}
        });

        // Pause => send progress
        player.on("pause", () => {
          try {
            if (!student?.student?.id || !questId || !chapterId) return;
            const duration = player.getDuration?.() || 0;
            const position = player.getPosition?.() || 0;
            sendVideoData({
              quest_id: String(questId || ""),
              chapter_id: String(chapterId || ""),
              video_duration: String(duration),
              video_watched_time: String(duration),
              video_completed: position >= duration ? 1 : 0,
            });
            setIsPaused && setIsPaused(true);
          } catch (e) {}
        });

        // Play => toggle paused
        player.on("play", () => {
          setIsPaused && setIsPaused(false);
        });

        // Complete => send progress + navigate/quiz/trophy logic
        player.on("complete", async () => {
          try {
            const duration = player.getDuration?.() || 0;
            await sendVideoData({
              quest_id: String(questId || ""),
              chapter_id: String(chapterId || ""),
              video_duration: String(duration),
              video_watched_time: String(duration),
              video_completed: Boolean(1),
            });

            let fetchedQuizzes = [];
            try {
              localStorage.removeItem("chapter");
              const response = await axiosInstance.get(
                `${TENANT_DOMAIN}/student/quest/chapter/quiz/${chapterId}`,
              );
              const data = response?.data?.data?.quiz;
              console.log("Quiz fetch data:", data);
              fetchedQuizzes = Array.isArray(data)
                ? data.map((item) => ({
                    question: item.question,
                    options: [
                      item.option1,
                      item.option2,
                      item.option3,
                      item.option4,
                    ].filter(Boolean),
                    correctAnswer: item.correctAnswer,
                    id: item.id,
                  }))
                : [];
            } catch (error) {
              console.error("Quiz fetch error:", error);
            }

            if (fetchedQuizzes.length > 0) {
              if (type === "quest") {
                navigate(`/student/admin/quiz/begin/${questId}/${chapterId}`);
              } else {
                navigate(`/student/admin/quiz/begin/${questId}/${chapterId}`);
              }
              if (nextEpisodeId === null)
                localStorage.setItem("chapter", "end");
              else localStorage.removeItem("chapter");
              return;
            }

            if (nextEpisodeId === null) {
              await sendAwardTrophy({
                studentId: String(student?.student?.id || ""),
                questId: String(questId || ""),
              });
              navigate(`/student/admin/episodes/${questId}`);
              localStorage.removeItem("chapter");
            } else {
              navigate(
                `/student/admin/episodes/detail/${questId}/${nextEpisodeId}`,
              );
              localStorage.removeItem("chapter");
            }
          } catch (e) {}
        });

        setupDebounceRef.current = null;
      })();
    }, 120);

    return () => {
      if (setupDebounceRef.current) {
        clearTimeout(setupDebounceRef.current);
        setupDebounceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, videoSources, student?.student?.id, questId, chapterId]);

  // ---------- Cleanup on unmount ----------
  useEffect(() => {
    return () => {
      try {
        destroyPlayer();
      } catch (e) {}
      try {
        document.body.style.overflow = "auto";
      } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- API helpers ----------
  const sendVideoData = async (data) => {
    try {
      await axiosInstance.post(`${TENANT_DOMAIN}/student/progress/video`, data);
    } catch (error) {
      console.error("Video progress error:", error);
    }
  };

  const sendAwardTrophy = async (data) => {
    if (!student?.student?.id) return;
    try {
      await axiosInstance.post(`${TENANT_DOMAIN}/student/award-trophy`, data);
    } catch (error) {
      console.error("Award trophy error:", error);
    }
  };

  // ---------- Modal actions ----------
  const handleQuiz = () => {
    if (nextEpisodeId === null) {
      localStorage.setItem("chapter", "end");
    } else localStorage.removeItem("chapter");

    if (type === "quest") {
      navigate(`/student/admin/quiz/begin/${questId}/${chapterId}`);
    } else {
      navigate(`/student/admin/quiz/begin/${questId}/${chapterId}`);
    }
    setShowModal(false);
  };

  const handleReplay = () => {
    try {
      if (playerInstance && playerInstance.seek) {
        playerInstance.seek(0);
        setShowModal(false);
      }
    } catch (e) {}
  };

  // ---------- UI ----------
  return (
    <div className="container-fluid p-0">
      {/* Top language selector: only shown when canChangeLocale is true */}
      {canChangeLocale && (
        <select
          onChange={(e) => {
            setPrevLanguage(language);
            setLanguage(e.target.value);
          }}
          value={language}
          className="VideoLangSlctr"
        >
          <option disabled>Language</option>
          <option value="en">English</option>
          <option value="hi">Hindi</option>
        </select>
      )}

      <div ref={playerContainerRef} />

      {errorMsg && (
        <div className="alert alert-danger mt-3" role="alert">
          {errorMsg}
        </div>
      )}

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        backdrop="static"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>You've nearly finished the video!</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Would you like to replay the video from the beginning or attempt the
          quiz?
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={handleReplay}>
            Replay from Start
          </button>
          <button className="btn btn-primary" onClick={handleQuiz}>
            Attempt Quiz
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

JWPlayerStudentMultiLanguage.propTypes = {
  videoSources: PropTypes.object,
  nextEpisodeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  videoStartTime: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  chapters: PropTypes.object,
  type: PropTypes.string,
  isPaused: PropTypes.bool,
  setIsPaused: PropTypes.func,
};

export default JWPlayerStudentMultiLanguage;
